import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

// Extracted form field component to reduce complexity
const FormField = ({ id, name, label, type = 'text', value, onChange, placeholder, required = false, testId }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs uppercase tracking-wider text-gray-400">
      {label}
    </Label>
    <Input
      id={id}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="bg-[#1c1c1c] border-white/10 text-white placeholder:text-gray-600 focus:border-[#C41E3A]"
      required={required}
      data-testid={testId}
    />
  </div>
);

// Extracted password field with toggle
const PasswordField = ({ value, onChange, showPassword, onToggle }) => (
  <div className="space-y-2">
    <Label htmlFor="password" className="text-xs uppercase tracking-wider text-gray-400">
      Password
    </Label>
    <div className="relative">
      <Input
        id="password"
        name="password"
        type={showPassword ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder="••••••••"
        className="bg-[#1c1c1c] border-white/10 text-white placeholder:text-gray-600 focus:border-[#C41E3A] pr-10"
        required
        data-testid="password-input"
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
      >
        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  </div>
);

// Extracted signup-only fields
const SignupFields = ({ formData, onChange }) => (
  <>
    <FormField
      id="username"
      name="username"
      label="Username"
      value={formData.username}
      onChange={onChange}
      placeholder="fashionista"
      required
      testId="username-input"
    />
    <FormField
      id="fullName"
      name="fullName"
      label="Full Name (Optional)"
      value={formData.fullName}
      onChange={onChange}
      placeholder="John Doe"
      testId="fullname-input"
    />
  </>
);

const AuthPage = () => {
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    username: '',
    fullName: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/feed');
    }
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        if (!formData.username) {
          toast.error('Username is required');
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.username, formData.fullName);
        toast.success('Account created successfully!');
      }
      navigate('/feed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const getSubmitButtonText = () => {
    if (loading) return 'Loading...';
    return isLogin ? 'Sign In' : 'Create Account';
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center px-6 bg-[#0a0a0a]" data-testid="auth-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-serif italic text-white mb-4">
            {isLogin ? 'Welcome Back' : 'Join the Community'}
          </h1>
          <p className="text-gray-400">
            {isLogin 
              ? 'Sign in to continue your fashion journey' 
              : 'Create an account to share your style'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {!isLogin && <SignupFields formData={formData} onChange={handleChange} />}

          <FormField
            id="email"
            name="email"
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@example.com"
            required
            testId="email-input"
          />

          <PasswordField
            value={formData.password}
            onChange={handleChange}
            showPassword={showPassword}
            onToggle={() => setShowPassword(!showPassword)}
          />

          <Button
            type="submit"
            disabled={loading}
            className="w-full btn-luxury btn-luxury-primary py-4"
            data-testid="auth-submit-btn"
          >
            {getSubmitButtonText()}
          </Button>
        </form>

        {/* Toggle */}
        <div className="mt-8 text-center">
          <p className="text-gray-400">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="ml-2 text-[#C41E3A] hover:underline"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
